<template>
  <div>
    <MockBar />
    <template v-if="$auth.user && apps">
      <Topbar v-bind="topbarProps" />
      <div v-if="maintenance">Maintenance</div>
      <component v-else :is="component" v-bind="wrappedProps" />
      <footer class="Footer">
        <nav class="Footer__tos">
          <p class="Footer__info">
            <span class="valipat">© 2022 Clarivate </span>
            <a href="https://clarivate.com/legal-center/terms-of-business/">Terms of Service</a>
          </p>
        </nav>
      </footer>
    </template>
    <LoginForm v-else :loading="loading" />
  </div>
</template>

<script>
import getThrottle from "@synbioz/throttle";
import canAccess from "../canAccess";
import LoginForm from "./LoginForm";
import Topbar from "./Topbar";
import MockBar from "./MockBar";

const throttle = getThrottle();

export default {
  props: {
    component: {
      type: Object,
      required: true
    },

    appKey: {
      type: String,
      required: true
    },

    authHost: {
      type: String,
      required: true
    },

    componentProps: {
      type: Object,
      default: () => {}
    },

    provideApps: {
      type: Boolean,
      default: false
    },

    statusesUpdateThrottle: {
      type: Number,
      default: 60000
    }
  },

  data() {
    return {
      loading: true,
      apps: {},
      maintenance: false
    };
  },

  computed: {
    topbarProps() {
      return {
        appKey: this.appKey,
        apps: this.apps,
        authHost: this.authHost
      };
    },

    wrappedProps() {
      return {
        ...this.componentProps,
        ...this.provideApps && {
          appKey: this.appKey,
          apps: this.apps
        }
      };
    }
  },

  watch: {
    $route: {
      handler() {
        this.updateStatuses();
      }
    }
  },

  methods: {
    updateStatuses() {
      this.handleAccess();

      throttle(() => {
        this.$auth.getApps()
          .then(apps => {
            this.apps = apps;
            this.handleAccess();
          });
      }, this.statusesUpdateThrottle);
    },

    handleAccess() {
      const key = this.appKey;

      this.maintenance = key in this.apps && !this.apps[key].enabled;

      if (!this.maintenance) {
        const app = this.apps[key];

        if (app && this.$auth.user && !canAccess(this.$auth.user.roles, app.excludedRoles)) {
          document.location.replace(this.apps.dashboard.url);
        }
      }
    }
  },

  async created() {
    await this.$auth.init();
    this.loading = false;
    this.updateStatuses();
  },

  components: { Topbar, LoginForm, MockBar }
};
</script>

<style lang="scss">
@import '~tesseract-ui/source/stylesheets/variables';

.fade-enter-active, .fade-leave-active {
  transition: opacity .2s;
  & > .Content,
  & > .Sidebar {
    position: absolute;
  }
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  & > .Content,
  & > .Sidebar {
    position: absolute;
  }
}

.Footer {
  margin: $margin auto;
  color: $clvt-black;
  text-align: center;
  line-height: 1.4;
  font-size: 14px;
  &__info {
    margin: 0;
  }
  .valipat {
    font-weight: bold;
  }
}

@media print {
  @page {
    size: landscape;
  }

  .MockBar,
  .Topbar,
  .Footer {
    display: none !important;
  }
}

// Allow better transition between apps
// Need to be applied in tesseract-ui
.Mainpage {
  position: relative;
}
.Content {
  margin: 0 auto;
  width: 100%;
  max-width: $main-width;
}
</style>
